// src/pages/NotificationsPage.jsx
import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";

export default function NotificationsPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "users"));
        const usersWithToken = snap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((u) => u.expoPushToken);
        setUsers(usersWithToken);
      } catch (e) {
        alert("Erreur lors de la récupération des utilisateurs: " + e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  const sendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      alert("Le titre et le message sont obligatoires");
      return;
    }
    if (selectedUsers.length === 0) {
      alert("Veuillez sélectionner au moins un destinataire");
      return;
    }
    setSending(true);

    try {
      const messages = selectedUsers.map((user) => ({
        to: user.expoPushToken,
        sound: "default",
        title,
        body,
        data: { extra: "notif" },
      }));

      const response = await fetch(
        "https://sunderdev.com/api/couture/send-notification",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(messages),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erreur serveur");
      }

      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Notification envoyée à ${selectedUsers.length} utilisateur${
        selectedUsers.length > 1 ? "s" : ""
      } !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);

      setTitle("");
      setBody("");
      setSelectedUsers([]);
    } catch (e) {
      alert("Erreur lors de l'envoi : " + e.message);
    }

    setSending(false);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...users]);
    }
  };

  const toggleUser = (user) => {
    const isSelected = selectedUsers.some((u) => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const UserSelectionModal = () => {
    const [searchTerm, setSearchTerm] = useState("");

    if (!showModal) return null;

    const filteredUsers = users.filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${user.firstname || ""} ${user.lastname || ""}`.toLowerCase();
      const email = (user.email || "").toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
          {/* Modal Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Sélectionner les destinataires
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-gray-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pl-10 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent"
                autoFocus
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 text-gray-400 absolute left-3 top-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                />
              </svg>
            </div>

            {/* Select All */}
            <div className="mt-4 flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">
                {selectedUsers.length} utilisateur{selectedUsers.length > 1 ? "s" : ""}{" "}
                sélectionné{selectedUsers.length > 1 ? "s" : ""} sur {users.length}
              </span>
              <button
                onClick={toggleSelectAll}
                className="text-sm font-medium text-[#801111] hover:text-[#5a0c0c]"
              >
                {selectedUsers.length === users.length
                  ? "Tout désélectionner"
                  : "Tout sélectionner"}
              </button>
            </div>
          </div>

          {/* Modal Body - User List */}
          <div className="flex-1 overflow-y-auto p-6 space-y-2">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-12 h-12 text-gray-400 mx-auto mb-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
                <p className="text-gray-600">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = selectedUsers.some((u) => u.id === user.id);
                return (
                  <div
                    key={user.id}
                    onClick={() => toggleUser(user)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? "bg-red-50 border-2 border-[#801111]"
                        : "bg-gray-50 border-2 border-transparent hover:border-gray-300"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? "bg-[#801111] border-[#801111]"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-4 h-4 text-white"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    {user.profilPhotoUrl ? (
                      <img
                        src={user.profilPhotoUrl}
                        alt={`${user.firstname} ${user.lastname}`}
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gradient-to-br from-[#801111] to-[#5a0c0c] rounded-full flex items-center justify-center text-white font-semibold">
                        {user.firstname?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.firstname && user.lastname
                          ? `${user.firstname} ${user.lastname}`
                          : "Nom non renseigné"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-6 border-t border-gray-200 flex gap-3">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 bg-[#801111] text-white py-2.5 rounded-lg font-medium hover:bg-[#5a0c0c] transition-colors"
            >
              Confirmer ({selectedUsers.length})
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-4xl mx-auto space-y-6">
          <UserSelectionModal />

          {/* Info Card */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#801111] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Envoyer une notification push
                </h3>
                <p className="text-sm text-gray-600">
                  Envoyez des notifications instantanées à vos utilisateurs. {users.length}{" "}
                  utilisateur{users.length > 1 ? "s ont" : " a"} activé les notifications.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stats Cards */}
            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Users avec notifs
                  </p>
                  <p className="text-3xl font-bold text-[#801111]">
                    {users.length}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-[#801111]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Destinataires
                  </p>
                  <p className="text-3xl font-bold text-rose-600">
                    {selectedUsers.length}
                  </p>
                </div>
                <div className="p-3 bg-rose-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-rose-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Type</p>
                  <p className="text-lg font-bold text-red-600">
                    {selectedUsers.length === users.length && selectedUsers.length > 0
                      ? "Broadcast"
                      : selectedUsers.length > 1
                      ? "Multiple"
                      : selectedUsers.length === 1
                      ? "Individuel"
                      : "-"}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-red-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Form Card */}
          <Card>
            <CardHeader>
              <CardTitle>Composer la notification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Recipient Selection Button */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Destinataires{" "}
                  <span className="text-xs text-gray-500 font-normal">
                    ({selectedUsers.length} sélectionné
                    {selectedUsers.length > 1 ? "s" : ""})
                  </span>
                </label>
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg hover:border-[#801111] hover:bg-red-50 transition-all group"
                  disabled={loading}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 text-[#801111]"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                        />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">
                        {selectedUsers.length === 0
                          ? "Sélectionner les destinataires"
                          : `${selectedUsers.length} utilisateur${
                              selectedUsers.length > 1 ? "s" : ""
                            } sélectionné${selectedUsers.length > 1 ? "s" : ""}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        Cliquez pour ouvrir la liste
                      </p>
                    </div>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-gray-400 group-hover:text-[#801111] transition-colors"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m8.25 4.5 7.5 7.5-7.5 7.5"
                    />
                  </svg>
                </button>

                {/* Selected Users Preview */}
                {selectedUsers.length > 0 && selectedUsers.length <= 5 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedUsers.slice(0, 5).map((user) => (
                      <div
                        key={user.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 text-[#801111] rounded-full text-xs font-medium"
                      >
                        {user.firstname && user.lastname
                          ? `${user.firstname} ${user.lastname}`
                          : user.email}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleUser(user);
                          }}
                          className="hover:bg-red-200 rounded-full p-0.5 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-3 h-3"
                          >
                            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={sending || loading}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Ex: Nouvelle fonctionnalité disponible !"
                  maxLength={65}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {title.length}/65 caractères
                </p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={5}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={sending || loading}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                  placeholder="Ex: Découvrez notre nouvelle fonctionnalité de recherche de véhicules perdus..."
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {body.length}/200 caractères
                </p>
              </div>

              {/* Preview Card */}
              {(title || body) && (
                <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">
                    Aperçu de la notification
                  </p>
                  <div className="bg-white rounded-lg shadow-lg p-4 max-w-sm">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#801111] rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-6 h-6 text-white"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">
                          {title || "Titre de la notification"}
                        </p>
                        <p className="text-gray-600 text-xs mt-1 line-clamp-3">
                          {body || "Message de la notification..."}
                        </p>
                        <p className="text-gray-400 text-xs mt-2">
                          À l'instant
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Send Button */}
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setTitle("");
                    setBody("");
                    setSelectedUsers([]);
                  }}
                  disabled={sending || loading}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={sendNotification}
                  disabled={
                    sending ||
                    loading ||
                    !title.trim() ||
                    !body.trim() ||
                    selectedUsers.length === 0
                  }
                  className="flex-1 bg-[#801111] text-white py-2.5 rounded-lg font-medium hover:bg-[#5a0c0c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                        />
                      </svg>
                      Envoyer la notification
                    </>
                  )}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}